import { Config } from '@backstage/config';
import { Entity } from '@backstage/catalog-model';
import {
  Coalition,
  CoalitionApp,
  CoalitionEnvOverride,
  CoalitionTemplates,
  CoalitionTier,
  CoalitionVar,
} from './types';

export const ENV_DASHBOARD_ANNOTATION = 'sparrow.io/env-dashboard';

// kebab-case coalition key on the annotation ↔ camelCase key on the
// config block. Backstage's frontend visibility filter drops values
// under index-signature maps, so each coalition must exist as a fixed
// named field in envDashboards (see packages/app/config.d.ts).
function toConfigKey(annotationValue: string): string {
  return annotationValue.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function readTier(t: Config): CoalitionTier {
  return {
    envs: t.getOptionalStringArray('envs') ?? [],
    cluster: t.getOptionalString('cluster') ?? '',
    namespaceOverride: t.getOptionalString('namespaceOverride'),
    lokiUid: t.getOptionalString('lokiUid') ?? '',
    mimirUid: t.getOptionalString('mimirUid') ?? '',
    eventsLokiUid: t.getOptionalString('eventsLokiUid') ?? '',
    lokiBaseUrl: t.getOptionalString('lokiBaseUrl') ?? '',
    mimirBaseUrl: t.getOptionalString('mimirBaseUrl') ?? '',
    metricsDashboardPath:
      t.getOptionalString('metricsDashboardPath') ??
      'd/k8s-pod-metrics/f09f9a80-k8s-pod-metrics',
    configRepoName: t.getOptionalString('configRepoName') ?? '',
    vaultBasePath: t.getOptionalString('vaultBasePath') ?? '',
    jenkinsBaseUrl: t.getOptionalString('jenkinsBaseUrl') ?? '',
    argocdBaseUrl: t.getOptionalString('argocdBaseUrl') ?? '',
    pipelines: t.getOptionalStringArray('pipelines'),
    templates: readOptionalTemplates(t.getOptionalConfig('templates')),
    vars: readVars(t),
    envOverrides: t.getOptionalConfigArray('envOverrides')?.map(readEnvOverride),
  };
}

function readVars(c: Config): CoalitionVar[] | undefined {
  return c.getOptionalConfigArray('vars')?.map(v => ({
    name: v.getString('name'),
    value: v.getString('value'),
  }));
}

function readEnvOverride(o: Config): CoalitionEnvOverride {
  return {
    env: o.getString('env'),
    namespace: o.getOptionalString('namespace'),
    cluster: o.getOptionalString('cluster'),
    lokiUid: o.getOptionalString('lokiUid'),
    mimirUid: o.getOptionalString('mimirUid'),
    jenkinsBaseUrl: o.getOptionalString('jenkinsBaseUrl'),
    argocdBaseUrl: o.getOptionalString('argocdBaseUrl'),
    pipelines: o.getOptionalStringArray('pipelines'),
    templates: readOptionalTemplates(o.getOptionalConfig('templates')),
    vars: readVars(o),
  };
}

function readApp(a: Config): CoalitionApp {
  return {
    name: a.getString('name'),
    jenkins: a.getOptionalString('jenkins'),
    config: a.getOptionalString('config'),
    vault: a.getOptionalString('vault'),
    repo: a.getOptionalString('repo'),
    feRepo: a.getOptionalString('feRepo'),
    container: a.getOptionalString('container'),
    hasBE: a.getOptionalBoolean('hasBE'),
    hasFE: a.getOptionalBoolean('hasFE'),
    hasVault: a.getOptionalBoolean('hasVault'),
    desc: a.getOptionalString('desc'),
    stagingNamespace: a.getOptionalString('stagingNamespace'),
    productionNamespace: a.getOptionalString('productionNamespace'),
    stagingArgocdApp: a.getOptionalString('stagingArgocdApp'),
    productionArgocdApp: a.getOptionalString('productionArgocdApp'),
    vars: readVars(a),
    // URL-driven mode fields are entity-side only — apps in central
    // config never set these (they use the coalition templates instead).
    // buildAppFromAnnotations reads them off the entity.
  };
}

function readTemplates(t: Config | undefined): CoalitionTemplates {
  if (!t) return {};
  return {
    repo: t.getOptionalString('repo'),
    config: t.getOptionalString('config'),
    vault: t.getOptionalString('vault'),
    argocd: t.getOptionalString('argocd'),
    argocdAppName: t.getOptionalString('argocdAppName'),
    jenkins: t.getOptionalString('jenkins'),
    jenkinsLinks: t.getOptionalConfigArray('jenkinsLinks')?.map(l => ({
      pipeline: l.getString('pipeline'),
      url: l.getString('url'),
    })),
    logsQuery: t.getOptionalString('logsQuery'),
    metricsDeployment: t.getOptionalString('metricsDeployment'),
    manifests: t.getOptionalString('manifests'),
  };
}

// Tier / env-override templates: undefined when the block is absent so
// the root templates apply unchanged.
function readOptionalTemplates(t: Config | undefined): CoalitionTemplates | undefined {
  return t ? readTemplates(t) : undefined;
}

// Read a single envDashboards.<key> block. Returns undefined if the
// annotation doesn't match a configured coalition.
export function readCoalition(
  configKey: string,
  root: Config,
): Coalition | undefined {
  const block = root.getOptionalConfig(`envDashboards.${configKey}`);
  if (!block) return undefined;

  const stagingBlock = block.getOptionalConfig('staging');
  const productionBlock = block.getOptionalConfig('production');
  if (!stagingBlock || !productionBlock) return undefined;

  return {
    key: configKey,
    staging: readTier(stagingBlock),
    production: readTier(productionBlock),
    configRepoOwner: block.getOptionalString('configRepoOwner') ?? '',
    configBranch: block.getOptionalString('configBranch') ?? 'main',
    vaultMount: block.getOptionalString('vaultMount') ?? '',
    vaultBaseUrl:
      root.getOptionalString('sparrow.vaultBaseUrl')?.replace(/\/$/, '') ?? '',
    pipelines: block.getOptionalStringArray('pipelines') ?? [],
    templates: readTemplates(block.getOptionalConfig('templates')),
    apps: (block.getOptionalConfigArray('apps') ?? []).map(readApp),
  };
}

// The one entry point components use. Reads the annotation off the
// entity, maps it to a config key, and returns the resolved coalition.
export function resolveCoalition(
  entity: Entity,
  config: Config,
): Coalition | undefined {
  const key = entity.metadata.annotations?.[ENV_DASHBOARD_ANNOTATION];
  if (!key) return undefined;
  return readCoalition(toConfigKey(key), config);
}

// Enumerate every env across both tiers, in staging-then-production
// order. Used by the selector bar to populate the dropdown.
export function allEnvs(coalition: Coalition): string[] {
  return [...coalition.staging.envs, ...coalition.production.envs];
}

// Given an env, return which tier it belongs to and the tier's config.
export function tierForEnv(
  env: string,
  coalition: Coalition,
): { tier: 'staging' | 'production'; cfg: CoalitionTier } | undefined {
  if (coalition.staging.envs.includes(env)) {
    return { tier: 'staging', cfg: coalition.staging };
  }
  if (coalition.production.envs.includes(env)) {
    return { tier: 'production', cfg: coalition.production };
  }
  return undefined;
}
